import React, { useCallback, useState } from 'react';
import type { ProxyTypeEnum } from 'venn-api';
import { Icon, Tooltip } from 'venn-ui-kit';
import { useHasFF } from 'venn-utils';
import SliderToggle from '../../../../slider-toggle/SliderToggle';
import { typeSupportsInterpolate } from '../utils';
import type { FundToBulkProxy } from '../../types';
import { isNil } from 'lodash';
import type { ExtrapolationToggleState } from '../hooks/types';
import { type LayeredDetailedProxyMetadata, getFirstProxy } from '../../../../hooks/useProxyDataQuery';

export type ExtrapolationToggleProps = {
  extrapolationToggleState: ExtrapolationToggleState;
  onToggle: () => void;
};

export const ExtrapolationToggle = ({ extrapolationToggleState, onToggle }: ExtrapolationToggleProps) => {
  const handleToggle = useCallback(() => {
    if (extrapolationToggleState.state === 'enabled') {
      onToggle();
    }
  }, [extrapolationToggleState.state, onToggle]);

  if (extrapolationToggleState.state === 'hidden') {
    return null;
  }

  return (
    <div
      className="flex flex-col font-bold text-[14px] leading-6 [&_.qa-toggle]:h-[35px]"
      data-testid="qa-proxy-extrapolation-toggle"
    >
      <div className="flex gap-[1.5px] items-center leading-[24px]">
        Extrapolation
        <Tooltip usePortal data-testid="qa-extrapolation-tooltip" content="Extend available history forward.">
          <Icon className="ml-[3px] text-venn-grey-80" type="circle-info" prefix="far" />
        </Tooltip>
      </div>
      <SliderToggle
        tooltipMessage={extrapolationToggleState.reason}
        disabled={extrapolationToggleState.state === 'disabled'}
        variant="new"
        toggled={extrapolationToggleState.isExtrapolated}
        onToggle={handleToggle}
      />
    </div>
  );
};

export function useExtrapolateToggle(
  investments: FundToBulkProxy[],
  selectedProxyType: ProxyTypeEnum | undefined,
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>,
) {
  const hasExtrapolationFf = useHasFF('extrapolation_ff');

  const [isExtrapolated, setIsExtrapolated] = useState(() => {
    return (
      !!investments.length &&
      investments.every((investment) => getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const))?.extrapolate)
    );
  });

  const shouldExtrapolate = hasExtrapolationFf && (selectedProxyType === 'EXTRAPOLATE' || isExtrapolated);

  const extrapolationToggleState: ExtrapolationToggleState = (() => {
    if (!hasExtrapolationFf) {
      return { state: 'hidden', isExtrapolated: false, reason: 'User does not have access to extrapolation.' };
    }

    if (isNil(selectedProxyType)) {
      return {
        state: 'disabled',
        isExtrapolated: false,
        reason: 'Select a proxy type to enable extrapolation.',
      };
    }

    if (selectedProxyType === 'EXTRAPOLATE') {
      return { state: 'hidden', isExtrapolated: false, reason: 'Extrapolation is already selected as the proxy type' };
    }

    if (selectedProxyType === 'SUBSTITUTE') {
      return {
        state: 'hidden',
        isExtrapolated: false,
        reason: 'Substitute is selected as the proxy type and supercedes extrapolation.',
      };
    }

    if (
      typeSupportsInterpolate(selectedProxyType) ||
      investments.some((investment) => {
        const proxyData = getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const));
        const frequency = proxyData?.unproxiedFrequency ?? investment.returnFrequency;
        return frequency !== 'QUARTERLY';
      })
    ) {
      return { state: 'enabled', isExtrapolated };
    }

    return {
      state: 'disabled',
      isExtrapolated: false,
      reason: "Quarterly investments can't be extrapolated without interpolation",
    };
  })();

  return {
    extrapolationToggleState,
    shouldExtrapolate,
    setIsExtrapolated,
  };
}
